import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Avatar,
  AvatarGroup,
  Chip,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import { ShareOutlined, Verified } from "@mui/icons-material";

import { formatDistanceToNow, parseISO } from "date-fns";
import Avatar_ from "./Avatar_";
import { ShareModal } from "./ShareModal";

const ListCard = ({
  jobId,
  title,
  company,
  desc,
  pay,
  jobType,
  speciality,
  role,
  applicants,
  onApplyClick,
  onViewClick,
  created_at,
  job,
}) => {
  const [open, setOpen] = React.useState(false);
  const [id, setId] = useState(null);

  const getTimeAgo = (updatedAt) => {
    const updatedDate = parseISO(updatedAt);
    return `Posted ${formatDistanceToNow(updatedDate, { addSuffix: true })}`;
  };

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "none",
        border: "1px solid #E9E9E9",
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
        backgroundColor: "white",
        "&:hover": {
          boxShadow: "0px 4px 15px 0px #0000001A",
          transform: "translateY(-1px)",
        },
      }}
    >
      <ShareModal
        open={open}
        handleClose={() => setOpen(false)}
        url={`${window.location.href}job-details/${id}`}
      />

      <CardContent sx={{ p: "32px" }}>
        {/* Header with role chip and share icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Chip
            label={role || speciality || "Echocardiography"}
            size="small"
            sx={{
              backgroundColor: "rgba(41, 155, 141, 0.1)",
              color: "rgba(33, 64, 75, 1)",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "32px",

              borderRadius: "20px",
              border: "1px solid #E0F2FE",
              "& .MuiChip-label": {
                px: 1.5,
              },
            }}
          />
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
              setId(jobId);
              // Handle share action
            }}
            sx={{
              p: 0.5,
              color: "#9CA3AF",
              transition: "color 0.2s",
              height: "40px",
              width: "40px",
              bgcolor: "rgba(41, 155, 141, 0.1)",
              borderRadius: "50%",
              "&:hover": {
                color: "#21404B",
                backgroundColor: "rgba(41, 155, 141, 0.1)",
              },
            }}
          >
            <ShareOutlined sx={{ fontSize: "1rem", color: "#21404B" }} />
          </IconButton>
        </Box>

        {/* Job title */}
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            lineHeight: 1.3,
            color: "#1a1a1a",
            mb: 2,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          {title}
        </Typography>

        {/* Job details in horizontal row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5 11.5C15.5 13.433 13.933 15 12 15C10.067 15 8.5 13.433 8.5 11.5C8.5 9.567 10.067 8 12 8C13.933 8 15.5 9.567 15.5 11.5Z"
                stroke="black"
              />
              <path
                d="M12 2.5C16.8706 2.5 21 6.53298 21 11.4258C21 16.3965 16.8033 19.8847 12.927 22.2567C12.6445 22.4162 12.325 22.5 12 22.5C11.675 22.5 11.3555 22.4162 11.073 22.2567C7.2039 19.8616 3 16.4137 3 11.4258C3 6.53298 7.12944 2.5 12 2.5Z"
                stroke="black"
              />
            </svg>
            <Typography
              variant="body2"
              sx={{
                color: "#1F2937",
                ontSize: "18px",
                fontWeight: 500,
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: "24px",
              }}
            >
              {jobType || "Locum"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.0065 22H9.60546C6.02021 22 4.22759 22 3.11379 20.865C2 19.7301 2 17.9034 2 14.25C2 10.5966 2 8.76992 3.11379 7.63496C4.22759 6.5 6.02021 6.5 9.60546 6.5H13.4082C16.9934 6.5 18.7861 6.5 19.8999 7.63496C20.7568 8.50819 20.9544 9.7909 21 12V13.5"
                stroke="black"
                stroke-linecap="round"
              />
              <path
                d="M19 19H16M16 22C14.3431 22 13 20.6569 13 19C13 17.3431 14.3431 16 16 16M19 22C20.6569 22 22 20.6569 22 19C22 17.3431 20.6569 16 19 16"
                stroke="black"
                stroke-linecap="round"
              />
              <path
                d="M16 6.5L15.9006 6.19094C15.4056 4.65089 15.1581 3.88087 14.5688 3.44043C13.9796 3 13.1969 3 11.6315 3H11.3684C9.80304 3 9.02035 3 8.43111 3.44043C7.84186 3.88087 7.59435 4.65089 7.09934 6.19094L7 6.5"
                stroke="black"
              />
            </svg>
            <Typography
              variant="body2"
              sx={{
                color: "#1F2937",
                ontSize: "18px",
                fontWeight: 500,
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: "24px",
              }}
            >
              {job?.client_city?.length > 50
                ? `${job?.client_city?.slice(0, 50)}...`
                : job?.client_city || "--"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 8H13.5C14.3284 8 15 8.67157 15 9.5M12 8H10.5C9.67157 8 9 8.67157 9 9.5V10C9 10.8284 9.67157 11.5 10.5 11.5H13.5C14.3284 11.5 15 12.1716 15 13V13.5C15 14.3284 14.3284 15 13.5 15H12M12 8V6.5M12 15H10.5C9.67157 15 9 14.3284 9 13.5M12 15V16.5"
                stroke="black"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M10.94 22.0124L9.02913 20.8073C8.54415 20.5014 8.30166 20.3485 8.03253 20.3397C7.74172 20.3301 7.49493 20.4768 6.97087 20.8073C6.38395 21.1774 5.21687 22.1971 4.46195 21.7108C4 21.4133 4 20.6575 4 19.1458V8.50002C4 5.67158 4 4.25736 4.82699 3.37868C5.65399 2.5 6.98501 2.5 9.64706 2.5H14.3529C17.015 2.5 18.346 2.5 19.173 3.37868C20 4.25736 20 5.67158 20 8.50002V19.1458C20 20.6575 20 21.4133 19.538 21.7108C18.7831 22.1971 17.6161 21.1774 17.0291 20.8073C16.5441 20.5014 16.3017 20.3485 16.0325 20.3397C15.7417 20.3301 15.4949 20.4768 14.9709 20.8073L13.06 22.0124C12.5445 22.3374 12.2868 22.5 12 22.5C11.7132 22.5 11.4554 22.3374 10.94 22.0124Z"
                stroke="black"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Typography
              variant="body2"
              sx={{
                color: "#1F2937",
                ontSize: "18px",
                fontWeight: 500,
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: "24px",
              }}
            >
              {job?.open_positions} Open positions
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        {desc && (
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              fontSize: "0.875rem",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mb: 2,
            }}
            dangerouslySetInnerHTML={{
              __html:
                desc && desc.length > 255 ? desc.slice(0, 255) + "..." : desc,
            }}
          />
        )}

        {/* Bottom section with buttons and applicant info */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 2,
            borderTop: "1px solid #E9E9E9",
          }}
        >
          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
            <Button
              onClick={onApplyClick}
              variant="contained"
              sx={{
                flex: 1,
                backgroundColor: "#21404B",
                color: "white",
                fontWeight: 500,
                fontSize: "18px",
                maxWidth: "196px",
                height: "56px",
                p: "16px 32px",
                fontFamily: "'Montserrat', sans-serif",
                borderRadius: "1000px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1F2937",
                  boxShadow: "none",
                },
              }}
            >
              Apply now &nbsp;
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M15 14V12C15 10.5858 15 9.87868 14.5607 9.43934C14.1213 9 13.4142 9 12 9H10M14 10L9 15"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </Button>

            <Button
              onClick={onViewClick}
              variant="outlined"
              sx={{
                flex: 1,
                color: "#374151",
                borderColor: "#D1D5DB",
                maxWidth: "166px",
                fontWeight: 500,

                p: "16px",
                fontFamily: "'Montserrat', sans-serif",
                borderRadius: "1000px",
                fontSize: "0.875rem",

                textTransform: "none",
                backgroundColor: "#21404B1A",
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                  borderColor: "#9CA3AF",
                  color: "#374151",
                },
              }}
            >
              View detail
            </Button>
          </Box>

          {/* Applicant info and time */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#A6A6A6",
                ontSize: "18px",
                fontWeight: 500,
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: "24px",
              }}
            >
              {applicants?.length > 0
                ? `${applicants.length}+ Applicant${
                    applicants.length !== 1 ? "s" : ""
                  }`
                : "48+ Applicants"}
            </Typography>

            <AvatarGroup
              max={3}
              sx={{
                "& .MuiAvatar-root": {
                  width: 24,
                  height: 24,
                  fontSize: "0.7rem",
                  border: "2px solid white",
                  marginLeft: "-8px",
                },
                "& .MuiAvatarGroup-avatar": {
                  backgroundColor: "#E5E7EB",
                  color: "#A6A6A6",
                  fontSize: "0.65rem",
                  fontWeight: 500,
                },
              }}
            >
              {applicants?.length > 0 ? (
                applicants
                  ?.slice(0, 3)
                  ?.map((applicant, index) => (
                    <Avatar_
                      name={applicant?.name}
                      photo={applicant.photo}
                      key={index}
                      sx={{ width: 24, height: 24 }}
                    />
                  ))
              ) : (
                <Avatar_ name={"John"} sx={{ width: 24, height: 24 }} />
              )}
            </AvatarGroup>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize: "0.875rem",
                color: "#A6A6A6",
              }}
            >
              {created_at ? getTimeAgo(created_at) : "Posted 1 hour ago"}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ListCard;
